import { useCallback, useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { toast } from "sonner";
import { Building2, Settings as SettingsIcon } from "lucide-react";
import { STORAGE_KEYS } from "@/lib/storage";
import { PeriodControls, TaxTotals, TaxTable, SettingsForm } from "@/components/TouristTaxParts";

function settingsFromProperty(p) {
  return {
    comune: p?.comune || "",
    provincia: p?.provincia || "",
    tourist_tax_rate: p?.tourist_tax_rate ?? 2.0,
    tourist_tax_max_nights: p?.tourist_tax_max_nights ?? 7,
  };
}

export default function TouristTax() {
  const { current: property, reload } = useProperty();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState(() => settingsFromProperty(property));

  // Sync property → editable form state. Intentional setState-in-effect
  // because `settings` is also user-editable in the form.
  useEffect(() => {
    if (property) setSettings(settingsFromProperty(property));
  }, [property]);

  const validPeriod = useCallback(() => {
    if (!from || !to) { toast.error("Seleziona un intervallo"); return false; }
    return true;
  }, [from, to]);

  const onLoad = useCallback(async () => {
    if (!validPeriod()) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tourist-tax/report?date_from=${from}&date_to=${to}`);
      setReport(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
    finally { setLoading(false); }
  }, [from, to, validPeriod]);

  const downloadCsv = useCallback(async (path, filename) => {
    if (!validPeriod()) return;
    try {
      const propertyId = localStorage.getItem(STORAGE_KEYS.propertyId);
      const res = await fetch(`${API}${path}?date_from=${from}&date_to=${to}`, {
        credentials: "include",
        headers: propertyId ? { "X-Property-Id": propertyId } : {},
      });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV scaricato");
    } catch { toast.error("Errore download"); }
  }, [from, to, validPeriod]);

  const onDownload = useCallback(
    () => downloadCsv("/tourist-tax/export", `tassa_soggiorno_${from}_${to}.csv`),
    [downloadCsv, from, to],
  );

  const onAuditDownload = useCallback(
    () => downloadCsv("/tourist-tax/audit-export", `audit_tassa_${from}_${to}.csv`),
    [downloadCsv, from, to],
  );

  const onSend = useCallback(async () => {
    if (!validPeriod()) return;
    try {
      const { data } = await api.post(`/tourist-tax/send?date_from=${from}&date_to=${to}`);
      toast.success(data.message);
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  }, [from, to, validPeriod]);

  const onSaveSettings = useCallback(async (e) => {
    e.preventDefault();
    if (!property) return;
    try {
      await api.put(`/properties/${property.id}`, {
        ...settings,
        tourist_tax_rate: Number(settings.tourist_tax_rate),
        tourist_tax_max_nights: Number(settings.tourist_tax_max_nights),
      });
      toast.success("Impostazioni aggiornate");
      setShowSettings(false);
      reload();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  }, [property, settings, reload]);

  return (
    <div className="p-6 md:p-12 max-w-6xl">
      <p className="text-xs tracked text-blue-700 mb-2">FISCO LOCALE</p>
      <div className="flex items-end justify-between mb-2 gap-4 flex-wrap">
        <h1 className="font-serif text-4xl tracking-tight">Tassa di soggiorno</h1>
        <button onClick={() => setShowSettings(!showSettings)} data-testid="toggle-tax-settings"
          className="inline-flex items-center gap-2 text-sm border border-stone-300 px-3 py-2 hover:bg-stone-50">
          <SettingsIcon className="w-4 h-4" /> Impostazioni struttura
        </button>
      </div>
      <p className="text-stone-600 mb-6">
        <strong>{property?.name}</strong> · Comune: <strong>{property?.comune || "—"}</strong>
        {property?.provincia ? ` (${property.provincia})` : ""} ·
        Tariffa <strong>€ {property?.tourist_tax_rate?.toFixed(2)}</strong> a notte/ospite ·
        Max <strong>{property?.tourist_tax_max_nights}</strong> notti
      </p>

      {showSettings && (
        <SettingsForm settings={settings} setSettings={setSettings} onSubmit={onSaveSettings} />
      )}

      <PeriodControls
        from={from} to={to}
        onFrom={(e) => setFrom(e.target.value)}
        onTo={(e) => setTo(e.target.value)}
        onLoad={onLoad} onDownload={onDownload} onAuditDownload={onAuditDownload} onSend={onSend}
        loading={loading} hasReport={!!report}
      />

      {report && (
        <>
          <TaxTotals totals={report.totals} />
          <TaxTable rows={report.rows} />
        </>
      )}

      <div className="mt-8 border border-stone-200 bg-stone-50 p-6 flex gap-4">
        <Building2 className="w-6 h-6 text-blue-700 flex-shrink-0" strokeWidth={1.5} />
        <div className="text-sm text-stone-700">
          <strong>Come funziona</strong>: il sistema calcola la tassa applicando la tariffa del tuo comune
          (modificabile in Impostazioni) per ogni ospite e notte, fino al massimo di notti tassabili. Esenzioni automatiche
          per età (configurata per struttura) e manuali per ospite (disabilità, lunga permanenza, ecc.).
        </div>
      </div>
    </div>
  );
}
